import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import type { HttpError } from "@refinedev/core";
import {
  PlusCircleSolidIcon,
  Button,
  CheckboxField,
  Field,
  Modal,
  SelectField,
  StatusBadge,
  type Column,
  type SelectOption,
} from "@fawkes/ui";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

// settings-pleeggezinnen: the React port of the Jmix `OpvangcategorieManagementPanel` — the
// "Opvangcategorieën" (care categories) sub-item of the Pleeggezinnen settings tile, shown only in a
// branch context. CRUD over the branch-nested `/api/vestigingen/{vestigingId}/opvangcategorieen`
// resource (the dataProvider prepends `/api`, so the urls here are RELATIVE).
//
// One card per species, over EVERY species — including the ones with no categories yet. The first
// migration made this a single flat table with a "Diersoort" column, which lost two things the Jmix
// screen had: the per-species "Pleeggezinnen actief" checkbox, and a species you have not used yet
// being visible at all. The checkbox is not cosmetic — `FosterPlacementService` offers no foster
// homes without an active row, and the capacity catalogue comes back empty — so losing it meant the
// setting could not be reached while two other screens kept gating on it.

export interface Opvangcategorie {
  id: number;
  diersoortId: number;
  diersoortNaam: string;
  naam: string;
  omschrijving: string | null;
  actief: boolean;
  sortering: number | null;
}

/** Een diersoort met de stand van "pleeggezinnen actief" binnen deze vestiging. */
interface PleeggezinDiersoort {
  diersoortId: number;
  diersoortNaam: string;
  actief: boolean;
}

interface EditState {
  id: number | null;
  diersoortId: string;
  naam: string;
  omschrijving: string;
  sortering: string;
  actief: boolean;
}

function emptyEdit(diersoortId?: number): EditState {
  return {
    id: null,
    // Voorgevuld als je vanuit een diersoortkaart toevoegt: daar heb je de keuze al gemaakt.
    diersoortId: diersoortId == null ? "" : String(diersoortId),
    naam: "",
    omschrijving: "",
    sortering: "",
    actief: true,
  };
}

/**
 * Een icoon per diersoort, zoals het oude scherm had.
 *
 * <p>Niet alleen versiering: met een kaart per diersoort vind je de kaart die je zoekt aan zijn
 * icoon voordat je de naam gelezen hebt. Een onbekende soort krijgt het pootje — dat is beter dan
 * geen icoon, want dan verspringt de kop van die ene kaart.
 */
const ICONEN: Record<string, string> = {
  hond: "🐶", kat: "🐱", konijn: "🐰", vogel: "🐦", hamster: "🐹", cavia: "🐹",
  paard: "🐎", vis: "🐟", reptiel: "🦎", schildpad: "🐢", kip: "🐔", geit: "🐐",
  schaap: "🐑", varken: "🐖", muis: "🐭", rat: "🐀",
};
function icoonVoor(naam: string): string {
  return ICONEN[naam.trim().toLowerCase()] ?? "🐾";
}

export function OpvangcategorieManagement({ vestigingId }: { vestigingId: number }) {
  const t = useTranslate();
  const base = `vestigingen/${vestigingId}/opvangcategorieen`;
  const { mutate: mutateCustom, isLoading: isSaving } = useCustomMutation();

  const [version, setVersion] = useState(0);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Welke diersoort er op dit moment omgezet wordt. Alleen die schakelaar gaat op slot; de andere
  // kaarten blijven bruikbaar, want ze staan los van elkaar.
  const [bezigMet, setBezigMet] = useState<number | null>(null);
  const [toggleFout, setToggleFout] = useState<string | null>(null);
  const refresh = () => setVersion((v) => v + 1);

  const { data: catData, isLoading, isError } = useCustom<Opvangcategorie[]>({
    url: base,
    method: "get",
    queryOptions: { queryKey: ["opvangcategorie", vestigingId, version] },
  });
  // De diersoortenlijst heeft zijn eigen laad- en foutstand. Eerst keek het scherm alleen naar die
  // van de categorieën, en dan werd een mislukte diersoortenlijst een lege lijst — met als melding
  // "Er zijn nog geen diersoorten geconfigureerd". Dat is precies de verkeerde boodschap: het leest
  // alsof de instellingen weg zijn, terwijl ze gewoon niet opgehaald konden worden.
  const { data: soortData, isLoading: soortenLaden, isError: soortenFout } =
    useCustom<PleeggezinDiersoort[]>({
      url: `${base}/pleeggezin-diersoorten`,
      method: "get",
      queryOptions: { queryKey: ["pleeggezin-diersoort", vestigingId, version] },
    });
  const rows = catData?.data ?? [];
  const diersoorten = soortData?.data ?? [];

  const diersoortOptions: SelectOption[] = [
    { value: "", label: t("instellingen.opvangcat.field.diersoortPlaceholder") },
    ...diersoorten.map((d) => ({ value: String(d.diersoortId), label: d.diersoortNaam })),
  ];

  function startCreate(diersoortId?: number) {
    setError(null);
    setEdit(emptyEdit(diersoortId));
  }
  function startEdit(row: Opvangcategorie) {
    setError(null);
    setEdit({
      id: row.id,
      diersoortId: String(row.diersoortId),
      naam: row.naam,
      omschrijving: row.omschrijving ?? "",
      sortering: row.sortering == null ? "" : String(row.sortering),
      actief: row.actief,
    });
  }

  const canSave = edit != null && edit.diersoortId !== "" && edit.naam.trim() !== "";

  function save() {
    if (!edit || !canSave) return;
    const values = {
      diersoortId: Number(edit.diersoortId),
      naam: edit.naam.trim(),
      omschrijving: edit.omschrijving.trim() === "" ? null : edit.omschrijving.trim(),
      actief: edit.actief,
      sortering: edit.sortering.trim() === "" ? null : Number(edit.sortering),
    };
    mutateCustom(
      { url: edit.id == null ? base : `${base}/${edit.id}`, method: edit.id == null ? "post" : "put", values },
      {
        onSuccess: () => {
          setEdit(null);
          refresh();
        },
        onError: () => setError(t("instellingen.opvangcat.saveFailed")),
      },
    );
  }

  function remove() {
    if (!edit?.id) return;
    mutateCustom(
      { url: `${base}/${edit.id}`, method: "delete", values: {} },
      {
        onSuccess: () => {
          setEdit(null);
          refresh();
        },
        onError: (err) => {
          const inUse = (err as HttpError)?.statusCode === 409;
          setError(t(inUse ? "instellingen.opvangcat.deleteInUse" : "instellingen.opvangcat.saveFailed"));
        },
      },
    );
  }

  /**
   * Pleeggezinnen aan- of uitzetten voor één diersoort.
   *
   * <p>Na afloop wordt de lijst opnieuw opgehaald in plaats van het vinkje lokaal te onthouden: dit
   * bepaalt of de plaatsdialoog pleeggezinnen aanbiedt, en dan hoort er te staan wat er echt is
   * opgeslagen. Mislukt het, dan springt het vinkje terug en staat erbij waarom.
   */
  function zetPleeggezinActief(d: PleeggezinDiersoort, actief: boolean) {
    setToggleFout(null);
    setBezigMet(d.diersoortId);
    mutateCustom(
      { url: `${base}/pleeggezin-diersoorten/${d.diersoortId}`, method: "put", values: { actief } },
      {
        onSuccess: () => {
          setBezigMet(null);
          refresh();
        },
        onError: () => {
          setBezigMet(null);
          setToggleFout(t("instellingen.opvangcat.pleeggezinFout").replace("{soort}", d.diersoortNaam));
        },
      },
    );
  }

  // De kolom "Diersoort" is weg: die staat nu in de kop van de kaart, en hem er twee keer bij zetten
  // maakt de tabel breder zonder iets toe te voegen.
  const columns: Column<Opvangcategorie>[] = [
    { header: t("instellingen.opvangcat.col.naam"), cell: (r) => r.naam },
    { header: t("instellingen.opvangcat.col.omschrijving"), cell: (r) => r.omschrijving ?? "" },
    {
      header: t("instellingen.opvangcat.col.status"),
      cell: (r) => (
        <StatusBadge tone={r.actief ? "success" : "neutral"}>
          {t(r.actief ? "instellingen.opvangcat.status.actief" : "instellingen.opvangcat.status.inactief")}
        </StatusBadge>
      ),
    },
    { header: t("instellingen.opvangcat.col.sortering"), cell: (r) => (r.sortering == null ? "" : r.sortering) },
    {
      header: t("instellingen.opvangcat.col.acties"),
      cell: (r) => (
        <Button variant="secondary" onClick={() => startEdit(r)}>
          {t("instellingen.opvangcat.edit")}
        </Button>
      ),
    },
  ];

  return (
    <div className="settings-opvangcat">
      <p className="settings-opvangcat__intro fawkes-muted">{t("instellingen.opvangcat.intro")}</p>

      {toggleFout && <p className="fawkes-error">{toggleFout}</p>}

      {isLoading || soortenLaden ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError || soortenFout ? (
        <p className="fawkes-error">{t("instellingen.opvangcat.loadFailed")}</p>
      ) : diersoorten.length === 0 ? (
        // Geen diersoorten geconfigureerd is iets anders dan geen categorieën: dan valt er hier
        // niets in te stellen en hoort dat er te staan in plaats van een leeg scherm.
        <p className="fawkes-muted">{t("instellingen.opvangcat.geenDiersoorten")}</p>
      ) : (
        <div className="settings-opvangcat__soorten">
          {diersoorten.map((d) => {
            const eigen = rows.filter((r) => r.diersoortId === d.diersoortId);
            return (
              <section
                className={"settings-opvangcat__soort"
                  + (eigen.length === 0 ? " settings-opvangcat__soort--leeg" : "")}
                key={d.diersoortId}
              >
                <header className="settings-opvangcat__soort-kop">
                  <span className="settings-opvangcat__soort-icoon" aria-hidden="true">
                    {icoonVoor(d.diersoortNaam)}
                  </span>
                  <h3 className="settings-opvangcat__soort-naam">{d.diersoortNaam}</h3>
                  <CheckboxField
                    label={t("instellingen.opvangcat.pleeggezinActief")}
                    checked={d.actief}
                    disabled={bezigMet === d.diersoortId}
                    onChange={(e) => zetPleeggezinActief(d, e.target.checked)}
                  />
                </header>

                {eigen.length === 0 ? (
                  <p className="fawkes-muted settings-opvangcat__soort-leeg">
                    {t("instellingen.opvangcat.geenCategorieen")}
                  </p>
                ) : (
                  <IndeelbareTabel tabelKey="settings-opvangcategorie-management"
                    columns={columns}
                    rows={eigen}
                    getRowKey={(r) => String(r.id)}
                    empty={<span className="fawkes-muted">{t("instellingen.opvangcat.empty")}</span>}
                  />
                )}

                <div className="settings-opvangcat__soort-voet">
                  <Button variant="secondary" onClick={() => startCreate(d.diersoortId)}>
                    <PlusCircleSolidIcon width={18} height={18} /> {t("instellingen.opvangcat.add")}
                  </Button>
                </div>
              </section>
            );
          })}
        </div>
      )}

      <Modal
        open={edit != null}
        title={edit?.id == null ? t("instellingen.opvangcat.create.title") : t("instellingen.opvangcat.edit.title")}
        onClose={() => setEdit(null)}
        footer={
          edit && (
            <>
              {edit.id != null && (
                <Button variant="secondary" onClick={remove} disabled={isSaving}>
                  {t("instellingen.opvangcat.delete")}
                </Button>
              )}
              <span style={{ flex: 1 }} />
              <Button variant="secondary" onClick={() => setEdit(null)} disabled={isSaving}>
                {t("common.cancel")}
              </Button>
              <Button variant="primary" onClick={save} disabled={isSaving || !canSave}>
                {t("common.save")}
              </Button>
            </>
          )
        }
      >
        {edit && (
          <>
            {error && <p className="fawkes-error">{error}</p>}
            <SelectField
              label={t("instellingen.opvangcat.field.diersoort")}
              options={diersoortOptions}
              value={edit.diersoortId}
              onChange={(e) => setEdit({ ...edit, diersoortId: e.target.value })}
            />
            <Field
              label={t("instellingen.opvangcat.field.naam")}
              value={edit.naam}
              maxLength={100}
              onChange={(e) => setEdit({ ...edit, naam: e.target.value })}
              required
            />
            <Field
              label={t("instellingen.opvangcat.field.omschrijving")}
              value={edit.omschrijving}
              maxLength={1000}
              onChange={(e) => setEdit({ ...edit, omschrijving: e.target.value })}
            />
            <Field
              label={t("instellingen.opvangcat.field.sortering")}
              type="number"
              value={edit.sortering}
              onChange={(e) => setEdit({ ...edit, sortering: e.target.value })}
            />
            <CheckboxField
              label={t("instellingen.opvangcat.field.actief")}
              checked={edit.actief}
              onChange={(e) => setEdit({ ...edit, actief: e.target.checked })}
            />
          </>
        )}
      </Modal>
    </div>
  );
}
